import { useState } from 'react';
import cn from 'classnames';
import Image from 'next/image';

import AriaTabPanel from 'react-aria-tabpanel';
import Selector from 'react-scale/components/tailwind/tab-selector/Selector';
import Item from 'react-scale/components/tailwind/tab-selector/Item';
import Video from 'react-scale/components/elements/video';
import { annotationIconsList } from 'react-scale/components/website/big/content/icons/AnnotationIcons';
import useDeviceDetect from 'react-scale/hooks/useDeviceDetect';

import ExampleContent from './ExampleContent';
import s from './by-annotation.module.css';

const ByAnnotation = ({
  tabs,
  landingHref,
  selectorStyle,
  product,
  highlightedButton = false,
  buttonText,
  color,
  theme = 'dark'
}) => {
  const [activeTab, setActiveTab] = useState(tabs[0].label);
  const [selectorLeft, setSelectorLeft] = useState(0);
  const [selectorWidth, setSelectorWidth] = useState(0);
  const { isMobile } = useDeviceDetect();

  const onClickSelector = (width, left) => {
    setSelectorWidth(width);
    setSelectorLeft(left);
  };

  const onChange = id => {
    setActiveTab(id);
  };

  return (
    <AriaTabPanel.Wrapper onChange={onChange}>
      <Selector
        selectorWidth={selectorWidth}
        bg={theme === 'dark' ? 'rgb(23, 23, 23)' : 'rgb(229, 231, 235)'}
        selectorLeft={selectorLeft}
        delayOnLoad={false}
      >
        {tabs.map(tab => {
          const Icon = annotationIconsList[tab.icon];
          const fill = (() => {
            if (theme === 'dark') {
              return activeTab === tab.label ? '#171717' : '#FFFFFF';
            }

            return activeTab === tab.label ? '#000' : '#4A5568';
          })();

          return (
            <Item
              isActive={activeTab === tab.label}
              label={tab.label}
              onClick={onClickSelector}
              key={`by-annotation-${tab.label}`}
              selectorStyle={selectorStyle}
              textColors={
                theme === 'dark' ? { active: 'text-black', inactive: 'text-white' } : undefined
              }
            >
              {tab.icon && (
                <div
                  className={cn(
                    'w-5 h-5 mr-2 shrink-0',
                    selectorStyle === 'small' ? 'lg:w-4 lg:h-4' : ''
                  )}
                >
                  <Icon className="transition-colors duration-700" fill={fill} />
                </div>
              )}
            </Item>
          );
        })}
      </Selector>
      {tabs.map(tab => (
        <AriaTabPanel.TabPanel tabId={tab.label} key={tab.label} active={activeTab === tab.label}>
          {activeTab === tab.label && (
            <>
              {tab.video && (
                <div className={s.fadeIn}>
                  <Video
                    className="mt-20 md:my-12 sm:mt-16 sm:mb-20 xl:mb-10"
                    smallButton={isMobile}
                    url={tab.video}
                  />
                </div>
              )}
              {tab.blurb && (
                <div className="flex flex-col lg:items-center -left-6 sm:left-0 relative lg:flex-row space-y-3 sm:space-y-0 bg-neutral-900 px-7 lg:px-[102px] py-8 sm:py-[46px] mb-20 mt-28 lg:my-10 sm:rounded-xl w-screen sm:w-full">
                  <div>
                    <div className="lg:!hidden px-2 pb-5">
                      <Image
                        width={67}
                        height={45}
                        alt={tab.blurb.icon.alt}
                        src={tab.blurb.icon.url}
                        layout="fixed"
                      />
                    </div>
                    <div className="hidden lg:block">
                      <Image
                        width={109}
                        height={73}
                        src={tab.blurb.icon.url}
                        alt={tab.blurb.icon.alt}
                        layout="fixed"
                      />
                    </div>
                  </div>
                  <div className="flex-1 pt-8 space-y-6 border-t lg:border-l lg:border-t-0 lg:pl-16 lg:py-0 lg:ml-16 border-tundora lg:space-y-4">
                    <p className="text-lg leading-8 sm:text-xl lg:text-2xl lg:leading-9">
                      {tab.blurb.title}
                    </p>
                    <p className="md:text-white leading-[30px] md:leading-8 pr-[14px] sm:pr-0 text-neutral-300 md:text-lg">
                      {tab.blurb.subtitle}
                    </p>
                  </div>
                </div>
              )}
              {tab.example && (
                <ExampleContent
                  instruction={tab.example.instruction}
                  code={tab.example.code}
                  response={tab.example.response}
                  example={tab.example.example}
                  result={tab.example.result}
                  responseDelay={tab.responseDelay || 1}
                  landingHref={landingHref}
                  highlightedButton={highlightedButton}
                  buttonText={buttonText}
                  product={product}
                  color={color}
                  theme={theme}
                />
              )}
              {tab.image && (
                <div className="mt-16">
                  <Image
                    layout="responsive"
                    quality="100"
                    loading="eager"
                    priority
                    src={tab.image}
                    alt={tab.label}
                    width={1139}
                    height={703}
                  />
                </div>
              )}
            </>
          )}
        </AriaTabPanel.TabPanel>
      ))}
    </AriaTabPanel.Wrapper>
  );
};

ByAnnotation.defaultProps = {
  selectorStyle: 'normal'
};

export default ByAnnotation;
